import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar, Copy, RefreshCw, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ICalLink({ userId, variant = 'compact' }) {
  const { t } = useLanguage();
  const [icalToken, setIcalToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchToken();
  }, []);

  const fetchToken = async () => {
    try {
      const res = await axios.get(`${API}/ical/token`);
      setIcalToken(res.data.ical_token);
    } catch (err) {
      console.error('Error fetching iCal token:', err);
    } finally {
      setLoading(false);
    }
  };

  const regenerateToken = async () => {
    if (!window.confirm('Régénérer le lien ? Les anciens liens ne fonctionneront plus.')) return;
    
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/ical/regenerate-token`);
      setIcalToken(res.data.ical_token);
      toast.success('Lien iCal régénéré');
    } catch (err) {
      toast.error('Erreur lors de la régénération');
    } finally {
      setRegenerating(false);
    }
  };

  const getIcalUrl = (useWebcal = false) => {
    if (!icalToken || !userId) return '';
    // Use production URL for calendar subscriptions
    const baseUrl = 'https://staff.vitalis-sports.ch';
    let url = `${baseUrl}/api/ical/${userId}/${icalToken}.ics`;
    
    // For iOS/macOS, use webcal:// protocol
    if (useWebcal) {
      url = url.replace(/^https?:\/\//, 'webcal://');
    }
    return url;
  };

  const copyToClipboard = async (useWebcal = false) => {
    const url = getIcalUrl(useWebcal);
    if (!url) {
      toast.error('Lien non disponible');
      return;
    }
    
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(useWebcal ? 'Lien webcal:// copié ✓' : 'Lien HTTPS copié ✓');
        return;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback:', err);
      }
    }
    
    // Fallback for older browsers or non-secure contexts
    try {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(useWebcal ? 'Lien webcal:// copié ✓' : 'Lien HTTPS copié ✓');
      } else {
        toast.error('Échec de la copie - copiez manuellement le lien');
      }
    } catch (err) {
      console.error('Copy fallback failed:', err);
      toast.error('Échec de la copie - copiez manuellement le lien');
    }
  };

  if (loading) {
    return null;
  }

  // Compact variant for dashboard
  if (variant === 'compact') {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
          className="flex items-center gap-2"
          data-testid="ical-link-btn"
        >
          <Calendar className="h-4 w-4" />
          Synchroniser calendrier
        </Button>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Synchronisation Calendrier
              </DialogTitle>
              <DialogDescription>
                Ajoutez votre planning Vitalis Sports à Google Calendar, Apple Calendar, Outlook, etc.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Lien iCal (HTTPS)</label>
                <div className="flex gap-2">
                  <Input
                    value={getIcalUrl(false)}
                    readOnly
                    className="text-xs font-mono"
                    data-testid="ical-url-input"
                  />
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(false)} title="Copier">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Pour Google Calendar, Outlook, Android</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Lien iCal (webcal://)</label>
                <div className="flex gap-2">
                  <Input
                    value={getIcalUrl(true)}
                    readOnly
                    className="text-xs font-mono"
                    data-testid="ical-url-webcal-input"
                  />
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(true)} title="Copier">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Pour iPhone / iPad / Mac (Apple Calendar)</p>
              </div>

              {/* iPhone Instructions */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
                  📱 iPhone / iPad
                </p>
                <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-decimal list-inside">
                  <li><strong>Méthode simple :</strong> Copiez le lien webcal://, ouvrez <strong>Safari</strong>, collez dans la barre d'adresse → iOS propose "S'abonner"</li>
                  <li><strong>Via Réglages :</strong> Réglages → Calendrier → Comptes → Ajouter un compte → Autre → Ajouter un cal. avec abonnement → Collez le lien</li>
                  <li><strong>Via l'app Calendrier :</strong> Calendrier → Calendriers (bas) → Ajouter un calendrier → Ajouter un calendrier avec abonnement</li>
                </ol>
              </div>

              {/* Android Instructions */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2 flex items-center gap-2">
                  🤖 Android (Google Calendar)
                </p>
                <ol className="text-sm text-green-700 dark:text-green-400 space-y-2 list-decimal list-inside">
                  <li>Copiez le lien <strong>HTTPS</strong> ci-dessus</li>
                  <li>Ouvrez <strong>Google Calendar</strong> sur ordinateur (calendar.google.com)</li>
                  <li>Paramètres (⚙️) → Ajouter un calendrier → À partir de l'URL</li>
                  <li>Collez le lien et cliquez "Ajouter un calendrier"</li>
                  <li>Le calendrier se synchronisera automatiquement sur votre téléphone Android</li>
                </ol>
                <p className="text-xs text-green-600 dark:text-green-400 mt-2 italic">
                  Note: L'ajout via URL n'est pas disponible dans l'app mobile, utilisez un ordinateur.
                </p>
              </div>

              {/* Outlook Instructions */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                  📧 Outlook
                </p>
                <ol className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
                  <li>Ouvrez <strong>Outlook</strong> (web ou desktop)</li>
                  <li>Calendrier → Ajouter un calendrier → S'abonner à partir du web</li>
                  <li>Collez le lien <strong>HTTPS</strong> et validez</li>
                </ol>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                  💡 Comment forcer la mise à jour ?
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1">
                  <li>• <strong>Google Calendar</strong> : Supprimer et ré-ajouter l'abonnement</li>
                  <li>• <strong>Apple Calendar</strong> : Clic droit → "Actualiser" (ou tirer vers le bas sur iPhone)</li>
                  <li>• <strong>Outlook</strong> : Onglet "Envoyer/Recevoir" → "Mettre à jour"</li>
                </ul>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 italic">
                  Note: Les apps calendrier synchronisent généralement toutes les 12-24h automatiquement.
                </p>
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Le calendrier inclut vos créneaux de travail et absences approuvées.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={regenerateToken}
                  disabled={regenerating}
                  className="text-xs"
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                  Régénérer
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Fermer
              </Button>
              <Button variant="outline" onClick={() => copyToClipboard(true)}>
                <Copy className="h-4 w-4 mr-2" />
                Copier (Apple)
              </Button>
              <Button onClick={() => copyToClipboard(false)}>
                <Copy className="h-4 w-4 mr-2" />
                Copier (Google/Outlook)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Full variant for profile page
  return (
    <div className="border border-border rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-primary" />
        <h3 className="font-medium">Synchronisation Calendrier</h3>
      </div>

      <p className="text-sm text-muted-foreground">
        Ajoutez votre planning à votre application de calendrier préférée (Google Calendar, Apple, Outlook...).
      </p>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Lien HTTPS (Google Calendar, Outlook)</label>
          <div className="flex gap-2">
            <Input
              value={getIcalUrl(false)}
              readOnly
              className="text-xs font-mono flex-1"
              data-testid="ical-url-input-profile"
            />
            <Button variant="outline" size="icon" onClick={() => copyToClipboard(false)} title="Copier">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Lien webcal:// (iPhone, iPad, Mac)</label>
          <div className="flex gap-2">
            <Input
              value={getIcalUrl(true)}
              readOnly
              className="text-xs font-mono flex-1"
              data-testid="ical-url-webcal-profile"
            />
            <Button variant="outline" size="icon" onClick={() => copyToClipboard(true)} title="Copier">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Utilisez ce lien pour les appareils Apple - il s'ouvre directement dans l'app Calendrier
          </p>
        </div>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        <p className="text-xs text-amber-700 dark:text-amber-400">
          ⚠️ Ce lien est personnel et donne accès à votre planning. Ne le partagez pas.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <a
          href={getIcalUrl(false)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3" />
          Tester le lien
        </a>
        <Button
          variant="ghost"
          size="sm"
          onClick={regenerateToken}
          disabled={regenerating}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
          Régénérer le lien
        </Button>
      </div>

      <div className="border-t pt-4 space-y-4">
        {/* iPhone Procedure */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm font-medium mb-3">📱 iPhone / iPad</p>
          <ol className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">1</span>
              <span>Allez dans <strong>Réglages</strong> → <strong>Calendrier</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">2</span>
              <span>Appuyez sur <strong>Comptes</strong> → <strong>Ajouter un compte</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">3</span>
              <span>Sélectionnez <strong>Autre</strong> → <strong>Ajouter un cal. avec abonnement</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">4</span>
              <span>Collez le lien <strong>webcal://</strong> ci-dessus et validez</span>
            </li>
          </ol>
        </div>

        {/* Android Procedure */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <p className="text-sm font-medium mb-3">🤖 Android (Google Calendar)</p>
          <ol className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">1</span>
              <span>Sur un <strong>ordinateur</strong>, allez sur <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">calendar.google.com</a></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">2</span>
              <span><strong>⚙️ Paramètres</strong> → <strong>Ajouter un calendrier</strong> → <strong>À partir de l'URL</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">3</span>
              <span>Collez le lien <strong>HTTPS</strong> ci-dessus</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">4</span>
              <span>Le calendrier se synchronisera automatiquement sur votre téléphone Android</span>
            </li>
          </ol>
        </div>
      </div>

      {/* Refresh frequency info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
          ⏱️ Fréquence de mise à jour automatique
        </p>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
          <li>• <strong>Apple Calendar (iPhone)</strong> : 15 min à quelques heures</li>
          <li>• <strong>Google Calendar</strong> : 12 à 24 heures</li>
          <li>• <strong>Outlook</strong> : 3 à 24 heures</li>
        </ul>
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-3 border-t border-blue-200 dark:border-blue-700 pt-2">
          💡 <strong>Forcer une mise à jour :</strong><br/>
          • iPhone : Réglages → Calendrier → Comptes → Récupération des données<br/>
          • Google : Supprimer et ré-ajouter l'abonnement
        </p>
      </div>
    </div>
  );
}
